import { useState, useEffect } from 'react';

interface Unit {
  title: string;
  description?: string;
}

type ResponsiveSize = number | { desktop: number; tablet: number; mobile: number };

interface Props {
  units?: Unit[];
  fontFamily?: string;
  fontSize?: ResponsiveSize;
  width?: string;
  height?: string;

  titleFontFamily?: string;
  descriptionFontFamily?: string;
  titleFontSize?: ResponsiveSize;
  descriptionFontSize?: ResponsiveSize;

  titleFontWeight?: string;
  descriptionFontWeight?: string;
  titleFontStyle?: 'normal' | 'italic';
  descriptionFontStyle?: 'normal' | 'italic';
  titleColor?: string;
  descriptionColor?: string;
}

function useDeviceType() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    function updateDevice() {
      if (window.innerWidth <= 768) {
        setDevice('mobile');
      } else if (window.innerWidth <= 1024) {
        setDevice('tablet');
      } else {
        setDevice('desktop');
      }
    }
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  return device;
}

function getResponsiveValue(value: ResponsiveSize | undefined, device: 'desktop' | 'tablet' | 'mobile') {
  if (typeof value === 'number' || value === undefined) return value;
  return value[device];
}

export function SELUnitDropdown({
  units = [],
  fontFamily = 'GT-Walsheim-Pro-Regular',
  fontSize = 24,
  width = '100%',
  height = 'auto',

  titleFontFamily,
  descriptionFontFamily,
  titleFontSize,
  descriptionFontSize,

  titleFontWeight = 'bold',
  descriptionFontWeight = 'normal',
  titleFontStyle = 'normal',
  descriptionFontStyle = 'normal',
  titleColor = '#e65300',
  descriptionColor = 'black',
}: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const device = useDeviceType();

  const resolvedFontSize = getResponsiveValue(fontSize, device);
  const resolvedTitleFontSize = getResponsiveValue(titleFontSize ?? fontSize, device);
  const resolvedDescriptionFontSize = getResponsiveValue(descriptionFontSize ?? fontSize, device);

  return (
    <div
      style={{
        fontFamily,
        fontSize: resolvedFontSize,
        lineHeight: '1.4',
        width,
        height,
        overflow: 'auto',
      }}
    >
      {units.map((unit, index) => {
        const isOpen = openIndex === index;
        return (
          <div key={index}>
            <div
              onClick={() => setOpenIndex(isOpen ? null : index)}
              style={{
                cursor: 'pointer',
                fontFamily: isOpen ? 'GT-Walsheim-Pro-Bold' : titleFontFamily || fontFamily,
                fontSize: resolvedTitleFontSize,
                fontWeight: isOpen ? titleFontWeight : 'normal',
                fontStyle: titleFontStyle,
                color: isOpen ? titleColor : 'black',
                marginTop: '0.5rem',
              }}
            >
              + {unit.title}
            </div>
            {isOpen && unit.description && (
              <div
                style={{
                  marginLeft: '1.5rem',
                  marginTop: '0.25rem',
                  marginBottom: '2rem',
                  fontFamily: descriptionFontFamily || fontFamily,
                  fontSize: resolvedDescriptionFontSize,
                  fontWeight: descriptionFontWeight,
                  fontStyle: descriptionFontStyle,
                  color: descriptionColor,
                }}
              >
                {unit.description}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
